import { ComponentsPlatformService } from '@wix/editor-platform-manifest-services';
import { CompRef } from '@wix/document-services-types';
import {
  hasAnyMeaningfulValue,
  isAllowedStylePresent,
} from '../common/actionValidators/actionVisibilityRules';
import { Meaningfulness } from '../common/meaningfulnessPredicates';
import { StylesDerivedNativeActions } from '@wix/editor-platform-host-integration-apis';
import {
  BORDER_CSS_PROPERTY_TYPES,
  BORDER_WIDTH_CSS_PROPERTY_TYPES,
} from '../constants';
import { DATA } from '@wix/component-protocol/schema';
import { hasDataType } from '../../../utils/helpers';

import { EditorPointer } from '@wix/editor-document-logic-api';

/**
 * Border action validator
 *
 * Rules:
 * - Single selection: Show if element has border in manifest (even with 0px)
 * - Multiple selections: Show if any element has border width > 0
 * - Fallback: Show if root has border in manifest and all widths are 0
 *
 * @param componentsAPI - Components platform service
 * @param editorPointers - Selected editor pointers
 * @returns true if border action should be shown
 */
export const createBorderValidator = () => {
  return (
    componentsAPI: ComponentsPlatformService,
    editorPointers: EditorPointer<CompRef>[],
  ): boolean => {
    const hasVectorArtData = hasDataType(
      componentsAPI.components.getData(editorPointers),
      DATA.DATA_TYPE.vectorArt,
    );

    if (hasVectorArtData) {
      return false;
    }

    const rootContexts = editorPointers.map((editorPointer) =>
      componentsAPI.buildManifestAPI({ editorPointers: [editorPointer] }),
    );

    const borderWidthProperties = new Set(BORDER_WIDTH_CSS_PROPERTY_TYPES);

    const borderCssProperties = new Set(BORDER_CSS_PROPERTY_TYPES);

    const borderProperties = new Set([
      ...borderWidthProperties,
      ...borderCssProperties,
    ]);

    for (const rootContext of rootContexts) {
      if (
        isAllowedStylePresent(rootContext, borderProperties) ||
        hasAnyMeaningfulValue(
          rootContext,
          borderWidthProperties,
          Meaningfulness.meaningCheck(StylesDerivedNativeActions.borderStyles), // Border rule: only non-zero is meaningful
        )
      ) {
        return true;
      }
    }

    return false;
  };
};
